import React from 'react';
import { Trophy, Users, Calendar, Clock, ChevronRight, Plus } from 'lucide-react';
import { MatchplayTournament } from '../../../types/matchplay-v2';
import { formatTournamentDate, formatLocalDate } from '../../../lib/matchplay-v2/utils';
import { QuickStart } from '../QuickStart';

interface TournamentListProps {
  tournaments: MatchplayTournament[];
  onTournamentSelect: (tournamentId: string) => void;
  onCreateTournament: () => void;
  isLoading: boolean;
  error: string | null;
}

export function TournamentList({
  tournaments,
  onTournamentSelect,
  onCreateTournament,
  isLoading,
  error
}: TournamentListProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700 border-green-200';
      case 'completed': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'setup': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getFormatIcon = (format: string) => {
    switch (format) {
      case 'singles': return Users;
      case 'fourball': return Users;
      case 'foursomes': return Users;
      default: return Trophy;
    }
  };

  const getFormatDisplayName = (format: string) => {
    switch (format) {
      case 'singles': return 'Singles';
      case 'fourball': return 'Four Ball';
      case 'foursomes': return 'Foursomes';
      default: return format;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Loading tournaments...</span>
        </div>
      </div>
    );
  }

  if (tournaments.length === 0) {
    return <QuickStart />;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Actions Header - Mobile Optimized */}
      <div className="text-center sm:text-left">
        <h2 className="text-lg font-semibold text-gray-900">Tournaments</h2>
        <p className="text-gray-600 text-sm sm:text-base">Select a tournament to view matches and scores</p>
      </div>

      {/* Tournament Grid - Mobile First */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {tournaments.map((tournament) => {
          const FormatIcon = getFormatIcon(tournament.format);
          
          return (
            <button
              key={tournament.id}
              onClick={() => onTournamentSelect(tournament.id)}
              className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 hover:shadow-lg hover:border-primary-300 transition-all duration-200 text-left group touch-manipulation active:scale-95"
            >
              {/* Tournament Header - Mobile Optimized */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="p-2 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors flex-shrink-0">
                    <FormatIcon className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 text-base sm:text-lg truncate">
                      {tournament.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {getFormatDisplayName(tournament.format)}
                    </p>
                  </div>
                </div>
                
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-500 transition-colors flex-shrink-0" />
              </div>

              {/* Tournament Details - Mobile Stacked */}
              <div className="space-y-3">
                {/* Status - Prominent on Mobile */}
                <div className="flex items-center justify-center sm:justify-between">
                  <span className="text-sm text-gray-600 hidden sm:block">Status:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(tournament.status)}`}>
                    {tournament.status.charAt(0).toUpperCase() + tournament.status.slice(1)}
                  </span>
                </div>

                {/* Date Info - Mobile Friendly */}
                <div className="space-y-2">
                  <div className="flex items-center justify-center sm:justify-between">
                    <span className="text-sm text-gray-600 hidden sm:block">Start:</span>
                    <span className="text-sm font-medium text-gray-900 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatTournamentDate(tournament.startDate)}
                    </span>
                  </div>

                  {/* End Date (if exists) */}
                  {tournament.endDate && (
                    <div className="flex items-center justify-center sm:justify-between">
                      <span className="text-sm text-gray-600 hidden sm:block">End:</span>
                      <span className="text-sm font-medium text-gray-900 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatTournamentDate(tournament.endDate)}
                      </span>
                    </div>
                  )}

                  {/* Created Date - Smaller on Mobile */}
                  <div className="flex items-center justify-center sm:justify-between">
                    <span className="text-xs text-gray-500 hidden sm:block">Created:</span>
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatLocalDate(tournament.createdAt)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Description - Mobile Optimized */}
              {tournament.description && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {tournament.description}
                  </p>
                </div>
              )}

              {/* Mobile Action Indicator */}
              <div className="mt-4 pt-3 border-t border-gray-100 sm:hidden">
                <div className="text-center text-xs text-gray-500 font-medium">
                  Tap to view tournament
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Empty State Helper - Mobile Friendly */}
      {tournaments.length === 0 && !isLoading && (
        <div className="text-center py-8 sm:py-12">
          <Trophy className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No tournaments yet</h3>
          <p className="text-gray-600 mb-6 text-sm sm:text-base">Create your first matchplay tournament to get started</p>
          <button
            onClick={onCreateTournament}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors touch-manipulation active:scale-95 font-medium"
          >
            <Plus className="w-5 h-5" />
            Create Tournament
          </button>
        </div>
      )}
    </div>
  );
} 